/* ============================================================================
 * manus-meta-browser-fix.js
 * ----------------------------------------------------------------------------
 * Hardens the cart progress bar (<free-shipping-bar>) and related cart UI
 * against errors observed in Meta in-app browsers (Facebook / Instagram
 * WebViews) which account for ~69% of TinyCraft traffic.
 *
 * Microsoft Clarity flagged 28% of JS errors as originating from the
 * FreeShippingBar custom element when run inside these WebViews. Root causes:
 *   1. Missing/blank `threshold` attribute on first cart re-render (calling
 *      `.replace()` on null throws "TypeError: Cannot read properties of null").
 *   2. `Intl.NumberFormat` throwing RangeError on certain locale strings
 *      (older WebKit-based WebViews shipped with locale-data gaps).
 *   3. `cart:change` event payload missing expected keys after the first
 *      add-to-cart in the WebView, causing reduce() to throw.
 *
 * This patch wraps the offending methods in try/catch with a graceful fallback
 * that hides (rather than breaks) the progress bar.
 *
 * Loaded from `layout/theme.liquid` after the main bundle:
 *   <script src="{{ 'manus-meta-browser-fix.js' | asset_url }}" defer></script>
 *
 * Edited 13-May-2026 by Manus.
 * ========================================================================= */

(function () {
  'use strict';

  // ---- 1. Detect Meta in-app browsers -------------------------------------
  var ua = (navigator.userAgent || '').toLowerCase();
  var isMetaWebView =
    /fban|fbav|fbios|instagram/.test(ua) || /\bmessenger\b/.test(ua);

  // ---- 2. Patch the FreeShippingBar custom element ------------------------
  // Wait for the custom element to be defined (theme.js loads it via defer).
  function patchFreeShippingBar() {
    if (!window.customElements || !window.customElements.get) return;
    var existing = window.customElements.get('free-shipping-bar');
    if (!existing) {
      // Custom element not registered yet — try again in a tick.
      return setTimeout(patchFreeShippingBar, 50);
    }

    var proto = existing.prototype;

    // Wrap attributeChangedCallback so a single bad attribute doesn't break
    // the entire cart drawer render.
    var origAttrChanged = proto.attributeChangedCallback;
    if (origAttrChanged) {
      proto.attributeChangedCallback = function () {
        try {
          return origAttrChanged.apply(this, arguments);
        } catch (e) {
          // Hide the bar gracefully so the rest of the cart still renders.
          try { this.style.display = 'none'; } catch (_) {}
          if (window.console && console.warn) {
            console.warn('[manus] FreeShippingBar.attributeChangedCallback threw, hiding bar:', e);
          }
        }
      };
    }

    // Wrap connectedCallback for the same reason.
    var origConnected = proto.connectedCallback;
    if (origConnected) {
      proto.connectedCallback = function () {
        try {
          return origConnected.apply(this, arguments);
        } catch (e) {
          try { this.style.display = 'none'; } catch (_) {}
          if (window.console && console.warn) {
            console.warn('[manus] FreeShippingBar.connectedCallback threw, hiding bar:', e);
          }
        }
      };
    }
  }

  // ---- 3. Polyfill Intl.NumberFormat fallback for ancient WebKit ----------
  // Some older Meta WebViews ship Intl.NumberFormat that throws on certain
  // locale arguments. Wrap the constructor to fall back to 'en-US' on error.
  if (window.Intl && window.Intl.NumberFormat) {
    var OrigNF = window.Intl.NumberFormat;
    try {
      // smoke-test
      new OrigNF(window.Shopify && Shopify.locale ? Shopify.locale : 'en-US', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      });
    } catch (e) {
      window.Intl.NumberFormat = function (locale, opts) {
        try {
          return new OrigNF(locale, opts);
        } catch (err) {
          try {
            return new OrigNF('en-US', opts);
          } catch (err2) {
            // Last-resort dummy that just stringifies numbers
            return {
              format: function (n) {
                return (typeof n === 'number' ? n.toFixed(2) : String(n));
              },
            };
          }
        }
      };
      window.Intl.NumberFormat.prototype = OrigNF.prototype;
    }
  }

  // ---- 4. Run patch on DOMContentLoaded -----------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchFreeShippingBar);
  } else {
    patchFreeShippingBar();
  }

  // Also run again after a delay in case theme.js loads asynchronously.
  setTimeout(patchFreeShippingBar, 500);
  setTimeout(patchFreeShippingBar, 2000);

  // ---- 5. Global guard: catch any uncaught error from the cart layer ------
  // Only swallow errors that clearly originate from the cart bar so we don't
  // mask real bugs elsewhere.
  window.addEventListener('error', function (ev) {
    if (!ev || !ev.message) return;
    var msg = String(ev.message);
    if (
      /free-shipping-bar/i.test(ev.filename || '') ||
      /FreeShippingBar/i.test(msg) ||
      /cart-progress-bar/i.test(msg)
    ) {
      ev.preventDefault();
      if (window.console && console.warn) {
        console.warn('[manus] Suppressed cart-bar error:', msg);
      }
    }
  });

  // Tag the body for analytics / CSS hooks if needed.
  if (isMetaWebView && document.body) {
    document.body.setAttribute('data-meta-webview', 'true');
  }
})();
